import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from '../../routing';
import { adminApi, commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import ListRow from '../../components/ListRow';
import { useIsMobile } from '../../utils/useIsMobile';
import { UserModal, ROLE_LABELS, ROLE_COLORS } from './modals';
import { LoadState, RoleBadge, InvitePill, LastLogin, formatDate, useRefetchOnFocus } from './parts';

// משתמשים — every user across every restaurant, searchable and filterable.
//
// Default order stays created_at DESC: for a support console, "who did I just
// create" is the commonest non-search entry.
//
// The route stays /people (English, like /customers); only the label is
// Hebrew. Renamed from "אנשים" — the section is about accounts and access,
// and "משתמשים" is the word already used on the customer card and in the
// search results, so one vocabulary now covers all three.

export default function PeoplePage() {
  const [people, setPeople] = useState(null);       // null = loading
  const [restaurants, setRestaurants] = useState([]);
  const [error, setError] = useState(false);
  const [modalUser, setModalUser] = useState(undefined); // undefined = closed, null = new
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Multi-value filters ride the URL as comma lists so an ops person can paste
  // a filtered view into Slack, same as every other list state here.
  const q = params.get('q') || '';
  const roles = useMemo(() => (params.get('role') || '').split(',').filter(Boolean), [params]);
  const restaurantIds = useMemo(() => (params.get('restaurant') || '').split(',').filter(Boolean), [params]);
  const pendingOnly = params.get('pending') === '1';

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    const v = Array.isArray(value) ? value.join(',') : value;
    if (v) next.set(key, v); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setError(false);
    try {
      const [p, r] = await Promise.all([commandCenterApi.getPeople(), adminApi.getRestaurants()]);
      setPeople(Array.isArray(p.data) ? p.data : []);
      setRestaurants(Array.isArray(r.data) ? r.data : []);
    } catch {
      // Say it failed. Never fall through to an empty list — an ops person
      // reading "no users" would go and create a duplicate account.
      setError(true);
      setPeople(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  const rows = useMemo(() => {
    if (!people) return [];
    const needle = q.trim().toLowerCase();
    return people.filter(u => {
      if (needle && !u.email.toLowerCase().includes(needle)) return false;
      if (roles.length && !roles.includes(u.role)) return false;
      // An admin matches EVERY restaurant filter: they reach every restaurant
      // without a user_restaurants row, so filtering them out here would
      // contradict both the customer card and the user count that links here.
      if (restaurantIds.length && u.role !== 'admin'
          && !restaurantIds.some(id => u.restaurant_ids?.includes(Number(id)))) return false;
      if (pendingOnly && u.invite?.state === 'active') return false;
      return true;
    });
  }, [people, q, roles, restaurantIds, pendingOnly]);

  // Options for the shared filter pills. Memoized on the source array so the
  // child's searchable filter doesn't re-run on every parent render.
  const roleOptions = useMemo(() => Object.keys(ROLE_LABELS), []);
  const restaurantOptions = useMemo(() => restaurants.map(r => String(r.id)), [restaurants]);
  const restaurantIdToName = useMemo(
    () => new Map(restaurants.map(r => [String(r.id), r.name])),
    [restaurants],
  );

  const resend = async (user) => {
    try {
      await commandCenterApi.resendInvite(user.id);
      toast(`נשלח קישור להגדרת סיסמה ל-${user.email}`);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שליחת המייל נכשלה', 'error');
    }
  };

  const restaurantCell = (u) => {
    if (u.role === 'admin') return <span className="cc-muted">כל המסעדות</span>;
    const list = u.restaurants || [];
    if (list.length === 0) return <span className="cc-muted">—</span>;
    // Count + first name + "ועוד N" rather than an untruncated comma soup.
    return (
      <>
        {list[0].name}
        {list.length > 1 && <span className="cc-muted"> · ועוד {list.length - 1}</span>}
      </>
    );
  };

  return (
    <>
      <h1 className="cc-page-title">משתמשים</h1>

      <div className="cc-card">
        <div className="cc-card-head" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ maxWidth: 240 }}
            value={q}
            onChange={e => setParam('q', e.target.value)}
            placeholder="סינון לפי אימייל"
            aria-label="סינון לפי אימייל"
          />
          {/* The same filter pills the /documents screen uses — multi-select,
              type-to-search, commits on click. A native <select> here would be
              a second, worse dropdown language in one product. */}
          <MultiSelectFilter
            label="תפקיד"
            values={roles}
            options={roleOptions}
            renderOption={(r) => ROLE_LABELS[r] || r}
            onChange={(next) => setParam('role', next)}
          />
          <MultiSelectFilter
            label="מסעדה"
            values={restaurantIds}
            options={restaurantOptions}
            renderOption={(id) => restaurantIdToName.get(id) || id}
            onChange={(next) => setParam('restaurant', next)}
            searchable
            minSearchChars={2}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={pendingOnly}
              onChange={e => setParam('pending', e.target.checked ? '1' : '')} />
            רק קישורי סיסמה פתוחים
          </label>
          <div className="cc-spacer" />
          <button className="btn btn-primary btn-sm" onClick={() => setModalUser(null)}>
            <Icon name="plus" size={14} /> משתמש חדש
          </button>
        </div>
      </div>

      <div className="cc-card">
        <LoadState
          loading={people === null && !error}
          error={error}
          onRetry={load}
          empty={rows.length === 0}
          emptyMessage={people?.length ? 'אין משתמשים שמתאימים לסינון' : 'אין משתמשים'}
        >
          {isMobile ? (
            // On a phone the table becomes the app's shared two-line row. Role
            // and access state lead the meta line — they're what an ops person
            // is checking when they look someone up from their pocket.
            <div className="m-list">
              {rows.map(u => (
                <ListRow
                  key={u.id}
                  primary={<span dir="ltr">{u.email}</span>}
                  meta={
                    <>
                      <RoleBadge role={u.role} />
                      <InvitePill invite={u.invite} />
                      <span>{u.role === 'admin' ? 'כל המסעדות' : (u.restaurants?.[0]?.name || '—')}</span>
                      {u.last_login_at && <span>· נכנס {formatDate(u.last_login_at)}</span>}
                    </>
                  }
                  onClick={() => navigate(`/command-center/people/${u.id}`)}
                />
              ))}
            </div>
          ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>אימייל</th>
                  <th>תפקיד</th>
                  <th>מסעדות</th>
                  <th>מצב</th>
                  <th>נוצר</th>
                  <th>התחברות אחרונה</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(u => (
                  <tr key={u.id} className="cc-row-link"
                    onClick={() => navigate(`/command-center/people/${u.id}`)}>
                    <td style={{ fontWeight: 500 }}>{u.email}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td>{restaurantCell(u)}</td>
                    <td>
                      <InvitePill invite={u.invite} />
                      {u.invite?.state !== 'active' && (
                        <button
                          className="btn btn-sm btn-secondary"
                          style={{ marginInlineStart: 6 }}
                          onClick={(e) => { e.stopPropagation(); resend(u); }}
                        >
                          <Icon name="send" size={13} /> שלח שוב
                        </button>
                      )}
                    </td>
                    <td className="cc-muted cc-num">{formatDate(u.created_at)}</td>
                    <td className="cc-muted"><LastLogin value={u.last_login_at} /></td>
                    <td className="cc-go"><Icon name="chevron-inline" size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </LoadState>
      </div>

      {modalUser !== undefined && (
        <UserModal
          user={modalUser}
          restaurants={restaurants}
          onSave={() => { setModalUser(undefined); load(); }}
          onClose={() => setModalUser(undefined)}
        />
      )}
    </>
  );
}

export { ROLE_LABELS, ROLE_COLORS };
